import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  TextField,
  ListItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import moment from 'moment';
import useDebouncedValue from '../../helper/useDebouncedValue';
import { getColor, getContrastColor, getNameInitials } from '../../utils/common';

export default function Sidebar({
  boards,
  selectedWhiteBoard,
  onSelect,
  onCreateOpen,
  onDelete,
  boardPermissions,
  isLoading
}) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query);

  const filtered = useMemo(() => {
    const filteredBoard = debouncedQuery.trim().toLowerCase();
    if (!filteredBoard) return boards;
    return boards.filter((b) => (b.name || '').toLowerCase().includes(filteredBoard));
  }, [boards, debouncedQuery]);

  return (
    <Box
      sx={{
        height: '100%',
        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header / Actions */}
      <Stack
        spacing={1}
        sx={{
          p: 1,
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        {
          boardPermissions?.includes("card_create") &&
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => onCreateOpen()}
            sx={{ width: '100%' }}
            disabled={isLoading}
          >
            Create whiteboard
          </Button>
        }

        {/* Optional search – remove this block if not needed */}
        <TextField
          size="small"
          placeholder="Search whiteboards…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Stack>

      <Divider />

      {/* Boards list */}
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, maxHeight: '63vh' }}>
        {filtered.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No whiteboards found.
            </Typography>
          </Box>
        ) : (
          <List dense>
            {filtered?.map((board) => {
              const selected = board?.id === selectedWhiteBoard?.id;
              return (
                <ListItem
                  key={board?.id}
                  disablePadding
                  secondaryAction={
                    boardPermissions?.includes("card_edit") ? (
                      <IconButton
                        edge="end"
                        aria-label={`Delete ${board?.name}`}
                        onClick={() => onDelete(board)}
                        size="small"
                        disabled={isLoading}
                      >
                        <DeleteOutlineOutlinedIcon
                          color={isLoading ? 'disabled' : 'error'}
                          fontSize='small'
                        />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemButton
                    selected={selected}
                    onClick={() => onSelect(board)}
                    disabled={isLoading}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: (theme) => theme.palette.action.selected,
                      },
                    }}
                  >
                    <Tooltip title={board?.name} placement='right'>
                      <Box
                        component={'span'}
                        sx={{
                          height: '26px',
                          width: '26px',
                          backgroundColor: getColor(board?.name),
                          color: getContrastColor(getColor(board?.name)),
                          p: 0.5,
                          mr: 1,
                          fontWeight: 'bold',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          textTransform: 'uppercase'
                        }}
                      >
                        {getNameInitials(board?.name)}
                      </Box>
                    </Tooltip>
                    <ListItemText
                      primary={board?.name}
                      secondary={
                        board?.updated_on ? `Updated ${moment(board.updated_on).format('MMM DD, YYYY')}` : undefined
                      }
                      primaryTypographyProps={{ noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box >
  );
}

Sidebar.propTypes = {
  boards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      updatedAt: PropTypes.string,
    })
  ).isRequired,
  selectedWhiteBoard: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onCreateOpen: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  boardPermissions: PropTypes.arrayOf(PropTypes.string).isRequired
};
